import React, { useState } from 'react'
import { Button, Icon, Intent, Menu, MenuItem, InputGroup } from '@blueprintjs/core'
import { useSegmentationPriorities } from '../../contexts/SegmentationPrioritiesProvider'
import { SegmentationDeletionDialog } from './SegmentationDeletionDialog'

/**
 * SegmentationsSelector renders a menu of available segmentations.
 *
 * It sorts segmentations based on score and displays a warning if scores are outdated.
 * Supports both single selection (original behavior) and multiple selection modes.
 *
 * @param {Object} props
 * @param {Function} props.selectSegmentation - Callback when a segmentation is selected (single mode).
 * @param {Object} props.selectedSegmentation - Currently selected segmentation (single mode).
 * @param {Function} props.onSelectionChange - Callback when segmentations are selected (multi mode).
 * @param {Array} props.selectedSegmentations - Currently selected segmentations (multi mode).
 * @param {boolean} props.multiSelect - Whether to enable multi-selection mode.
 * @param {boolean} props.showSearch - Whether to show the search filter.
 * @returns {JSX.Element} The rendered menu.
 */
export function SegmentationsSelector({
  selectSegmentation,
  selectedSegmentation,
  onSelectionChange,
  selectedSegmentations = [],
  multiSelect = false,
  showSearch = false,
}) {
  const { segmentations, segmentationsScores, needsRecalculationFlag } =
    useSegmentationPriorities()

  // Local state for search functionality
  const [searchQuery, setSearchQuery] = useState('')
  const [deletionTarget, setDeletionTarget] = useState(null)
  const [showBatchDeletion, setShowBatchDeletion] = useState(false)

  // Enhance segmentations with their respective score, if available.
  const segmentationsWithScore = segmentations.map(seg => {
    const matchingScoreObj = segmentationsScores?.find(
      score => score.id === seg.id
    )
    return {
      ...seg,
      segmentationScore: matchingScoreObj
        ? matchingScoreObj.segmentation_score
        : null,
    }
  })

  // Sort segmentations so that those with valid scores are displayed first.
  const sortedSegmentations = segmentationsWithScore.sort((a, b) => {
    if (a.segmentationScore === null && b.segmentationScore !== null) return 1
    if (a.segmentationScore !== null && b.segmentationScore === null) return -1
    if (a.segmentationScore !== null && b.segmentationScore !== null) {
      return b.segmentationScore - a.segmentationScore
    }
    return 0
  })

  // Filter segmentations based on search query
  const filteredSegmentations = sortedSegmentations.filter(segmentation => {
    if (!searchQuery) return true

    const searchTerm = searchQuery.toLowerCase()
    const segmentationName = (segmentation.variable?.name || '').toLowerCase()
    const segmentationLabel = (segmentation.name || '').toLowerCase()

    return (
      segmentationName.includes(searchTerm) ||
      segmentationLabel.includes(searchTerm)
    )
  })

  // Handle segmentation selection logic
  const handleSegmentationClick = (segmentation, event) => {
    if (!multiSelect) {
      // Single selection mode (original behavior)
      selectSegmentation?.(segmentation)
      return
    }

    // Multi-selection mode
    const isSelected = selectedSegmentations.some(s => s.id === segmentation.id)

    if (event?.ctrlKey || event?.metaKey) {
      // CTRL/CMD + Click: toggle selection
      const newSelection = isSelected
        ? selectedSegmentations.filter(s => s.id !== segmentation.id)
        : [...selectedSegmentations, segmentation]
      onSelectionChange?.(newSelection)
    } else if (event?.shiftKey && selectedSegmentations.length > 0) {
      // SHIFT + Click: range selection
      const lastSelectedIndex = filteredSegmentations.findIndex(
        s => s.id === selectedSegmentations[selectedSegmentations.length - 1].id
      )
      const currentIndex = filteredSegmentations.findIndex(
        s => s.id === segmentation.id
      )

      if (lastSelectedIndex !== -1 && currentIndex !== -1) {
        const start = Math.min(lastSelectedIndex, currentIndex)
        const end = Math.max(lastSelectedIndex, currentIndex)
        const rangeSelection = filteredSegmentations.slice(start, end + 1)

        // Merge with existing selection, avoiding duplicates
        const newSelection = [...selectedSegmentations]
        rangeSelection.forEach(seg => {
          if (!newSelection.some(s => s.id === seg.id)) {
            newSelection.push(seg)
          }
        })
        onSelectionChange?.(newSelection)
      }
    } else {
      // Normal click: single selection or toggle if already selected
      const newSelection = isSelected ? [] : [segmentation]
      onSelectionChange?.(newSelection)
      // Also trigger single-select for info tab detail loading
      selectSegmentation?.(isSelected ? null : segmentation)
    }
  }

  return (
    <div
      style={{
        height: '100%',
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Search input (only shown when showSearch is true) */}
      {showSearch && (
        <div style={{ padding: '10px', borderBottom: '1px solid #ccc' }}>
          <InputGroup
            leftIcon="search"
            placeholder="Search segmentations..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            small={true}
          />
        </div>
      )}

      <Menu
        style={{
          height: '100%',
          maxHeight: '100%',
          overflowY: 'auto',
          flex: 1,
        }}
      >
        {/* Show a warning if segmentation scores are outdated */}
        {needsRecalculationFlag && segmentationsScores.length > 0 && (
          <MenuItem
            text={
              <span style={{ color: 'orange', fontStyle: 'italic' }}>
                Segmentation scores need to be recalculated{' '}
                <Icon icon="warning-sign" />
              </span>
            }
            disabled={true}
          />
        )}

        {filteredSegmentations.map(segmentation => {
          // Determine if this segmentation is selected
          const isSelected = multiSelect
            ? selectedSegmentations.some(s => s.id === segmentation.id)
            : selectedSegmentation &&
              selectedSegmentation.id === segmentation.id

          return (
            <MenuItem
              key={segmentation.id}
              text={segmentation.variable.name}
              // Display score as a percentage if available.
              label={
                segmentation.segmentationScore !== null ? (
                  <span
                    style={{
                      color: needsRecalculationFlag ? 'orange' : 'inherit',
                    }}
                  >
                    {(segmentation.segmentationScore * 100).toFixed(3)}%
                  </span>
                ) : (
                  ''
                )
              }
              labelElement={
                isSelected && (
                  <Button
                    icon="trash"
                    minimal
                    small
                    intent={Intent.DANGER}
                    onClick={e => {
                      e.stopPropagation()
                      setDeletionTarget(segmentation)
                    }}
                  />
                )
              }
              active={isSelected}
              onClick={event => handleSegmentationClick(segmentation, event)}
            />
          )
        })}

        {filteredSegmentations.length === 0 && searchQuery && (
          <MenuItem
            text={`No segmentations found for "${searchQuery}"`}
            disabled={true}
          />
        )}
      </Menu>

      {multiSelect && selectedSegmentations.length >= 2 && (
        <div
          style={{
            padding: '6px 10px',
          }}
        >
          <Button
            icon="trash"
            intent={Intent.DANGER}
            text={`Delete Selected (${selectedSegmentations.length})`}
            small
            fill
            onClick={() => setShowBatchDeletion(true)}
          />
        </div>
      )}

      <SegmentationDeletionDialog
        isOpen={showBatchDeletion}
        onClose={() => setShowBatchDeletion(false)}
        segmentations={selectedSegmentations}
        onDeleted={() => {
          setShowBatchDeletion(false)
          onSelectionChange?.([])
          selectSegmentation?.(null)
        }}
      />

      <SegmentationDeletionDialog
        isOpen={deletionTarget !== null}
        onClose={() => setDeletionTarget(null)}
        segmentations={deletionTarget ? [deletionTarget] : []}
        onDeleted={() => {
          setDeletionTarget(null)
          // If the deleted segmentation was selected, clear selection
          if (
            selectedSegmentation &&
            selectedSegmentation.id === deletionTarget?.id
          ) {
            selectSegmentation?.(null)
          }
          if (multiSelect && deletionTarget) {
            const newSelection = selectedSegmentations.filter(
              s => s.id !== deletionTarget.id
            )
            onSelectionChange?.(newSelection)
          }
        }}
      />
    </div>
  )
}
